import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Slider,
  Switch,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import VolumeUpIcon from "@material-ui/icons/VolumeUp";

import { i18n } from "../../translate/i18n";
import { useNotificationSound } from "../../context/NotificationSound";

const useStyles = makeStyles(theme => ({
  section: {
    marginBottom: theme.spacing(3),
  },
  formControl: {
    width: "100%",
  },
  volumeHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(1),
  },
  volumeValue: {
    minWidth: 48,
    textAlign: "right",
    fontWeight: 700,
    color: theme.palette.primary.main,
  },
  helperText: {
    marginTop: theme.spacing(1),
    color: theme.palette.text.secondary,
  },
  testButton: {
    marginRight: "auto",
  },
}));

const NotificationSoundSettingsModal = ({ open, onClose }) => {
  const classes = useStyles();
  const {
    settings,
    saveSettings,
    previewNotificationSound,
  } = useNotificationSound();
  const [draftSettings, setDraftSettings] = useState(settings);

  useEffect(() => {
    if (open) setDraftSettings(settings);
  }, [open, settings]);

  const updateDraft = updates => {
    setDraftSettings(previous => ({ ...previous, ...updates }));
  };

  const handleSave = () => {
    saveSettings(draftSettings);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{i18n.t("notificationSound.title")}</DialogTitle>
      <DialogContent dividers>
        <div className={classes.section}>
          <FormControlLabel
            control={
              <Switch
                checked={draftSettings.enabled}
                onChange={event =>
                  updateDraft({ enabled: event.target.checked })
                }
                color="primary"
              />
            }
            label={i18n.t("notificationSound.enabled")}
          />
          <Typography variant="body2" className={classes.helperText}>
            {i18n.t("notificationSound.enabledHelp")}
          </Typography>
        </div>

        <div className={classes.section}>
          <FormControl variant="outlined" className={classes.formControl}>
            <InputLabel id="notification-tone-label">
              {i18n.t("notificationSound.tone")}
            </InputLabel>
            <Select
              labelId="notification-tone-label"
              value={draftSettings.tone}
              onChange={event => updateDraft({ tone: event.target.value })}
              label={i18n.t("notificationSound.tone")}
            >
              <MenuItem value="soft">
                {i18n.t("notificationSound.tones.soft")}
              </MenuItem>
              <MenuItem value="discreet">
                {i18n.t("notificationSound.tones.discreet")}
              </MenuItem>
              <MenuItem value="classic">
                {i18n.t("notificationSound.tones.classic")}
              </MenuItem>
            </Select>
          </FormControl>
        </div>

        <div className={classes.section}>
          <div className={classes.volumeHeader}>
            <Typography>{i18n.t("notificationSound.volume")}</Typography>
            <span className={classes.volumeValue}>{draftSettings.volume}%</span>
          </div>
          <Slider
            value={draftSettings.volume}
            onChange={(event, value) => updateDraft({ volume: value })}
            min={0}
            max={100}
            step={5}
            valueLabelDisplay="auto"
            aria-label={i18n.t("notificationSound.volume")}
          />
        </div>

        <div className={classes.section}>
          <FormControl variant="outlined" className={classes.formControl}>
            <InputLabel id="notification-cooldown-label">
              {i18n.t("notificationSound.repeatInterval")}
            </InputLabel>
            <Select
              labelId="notification-cooldown-label"
              value={draftSettings.cooldownSeconds}
              onChange={event =>
                updateDraft({ cooldownSeconds: Number(event.target.value) })
              }
              label={i18n.t("notificationSound.repeatInterval")}
            >
              <MenuItem value={0}>
                {i18n.t("notificationSound.intervals.everyMessage")}
              </MenuItem>
              <MenuItem value={5}>5 {i18n.t("notificationSound.seconds")}</MenuItem>
              <MenuItem value={8}>8 {i18n.t("notificationSound.seconds")}</MenuItem>
              <MenuItem value={15}>15 {i18n.t("notificationSound.seconds")}</MenuItem>
              <MenuItem value={30}>30 {i18n.t("notificationSound.seconds")}</MenuItem>
            </Select>
          </FormControl>
          <Typography variant="body2" className={classes.helperText}>
            {i18n.t("notificationSound.repeatHelp")}
          </Typography>
        </div>
      </DialogContent>
      <DialogActions>
        <Button
          className={classes.testButton}
          startIcon={<VolumeUpIcon />}
          onClick={() => previewNotificationSound(draftSettings)}
          disabled={draftSettings.volume === 0}
          color="primary"
        >
          {i18n.t("notificationSound.test")}
        </Button>
        <Button onClick={onClose} color="default">
          {i18n.t("notificationSound.cancel")}
        </Button>
        <Button onClick={handleSave} color="primary" variant="contained">
          {i18n.t("notificationSound.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NotificationSoundSettingsModal;

